import { useRef, useState } from "react";
import {
    Dialog,
    Portal,
    Box,
    Text,
    Input,
    Textarea,
    Button,
    Grid,
    VStack,
    HStack,
    Flex,
} from "@chakra-ui/react";
import {
    Users,
    Wallet,
    User,
    Bug,
    Upload,
    X,
    Ticket
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { createSupportTicketAction } from "../../../state/actions/supportActions";

import { toaster } from "../../../components/toaster";

import ActivityToast from "../../../components/ActivityToast";

const categories = [
    {
        label: "Recruitment & Referrals",
        icon: Users,
        value:
            "RECRUITMENT_AND_REFERRALS",
    },
    {
        label: "Commissions & Payouts",
        icon: Wallet,
        value:
            "COMMISSIONS_AND_PAYOUTS",
    },
    {
        label: "Account & Profile",
        icon: User,
        value:
            "ACCOUNT_AND_PROFILE",
    },
    {
        label: "Technical Support",
        icon: Bug,
        value:
            "TECHNICAL_SUPPORT",
    },
];

const SupportTicketModal = ({
    isOpen,
    onClose,
    category,
    setCategory,
}) => {
    const [files, setFiles] = useState([]);
    const dispatch = useDispatch();

const { creating } = useSelector(
    (state) => state.support
);

const [subject, setSubject] =
    useState("");

const [message, setMessage] =
    useState("");

    const fileInputRef = useRef(null);
    const MAX_FILES = 6;

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(
            e.target.files || []
        );

        const processedFiles =
            selectedFiles.map((file) => ({
                file,
                preview:
                    file.type.startsWith(
                        "image/"
                    )
                        ? URL.createObjectURL(
                            file
                        )
                        : null,
            }));

        setFiles((prev) =>
            [
                ...prev,
                ...processedFiles,
            ].slice(0, MAX_FILES)
        );
    };
    const removeFile = (indexToRemove) => {
        setFiles((prev) =>
            prev.filter(
                (_, index) =>
                    index !== indexToRemove
            )
        );
    };

    const handleSubmit =
    async () => {
        if (
            !subject.trim() ||
            !message.trim()
        ) {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="error"
                        title="Missing Information"
                        description="Subject and message are required."
                        timestamp="Just now"
                    />
                ),
            });

            return;
        }

        const formData =
            new FormData();

        formData.append(
    "category",
    category
);;

        formData.append(
            "subject",
            subject
        );

        formData.append(
            "message",
            message
        );

        files.forEach(
            (item) => {
                formData.append(
                    "files",
                    item.file
                );
            }
        );

        const response =
            await dispatch(
                createSupportTicketAction(
                    formData
                )
            );

        if (
            response?.success
        ) {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="success"
                        title="Ticket Submitted"
                        description="Your support ticket has been submitted successfully."
                        timestamp="Just now"
                    />
                ),
            });

            setSubject("");
            setMessage("");
            setFiles([]);

            onClose();
        } else {
            toaster.create({
                render: () => (
                    <ActivityToast
                        type="error"
                        title="Submission Failed"
                        description={
                            response?.error ||
                            "Failed to create support ticket"
                        }
                        timestamp="Just now"
                    />
                ),
            });
        }
    };
    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(e) => {
                if (!e.open) {
                    onClose();
                }
            }}
        >
            <Portal>
                <Dialog.Backdrop
                    bg="blackAlpha.600"
                    backdropFilter="blur(4px)"
                />

                <Dialog.Positioner>
                    <Dialog.Content
                        w={{
                            base: "calc(100vw - 24px)",
                            md: "720px",
                            lg: "760px",
                        }}
                        maxW="760px"
                        borderRadius={{
                            base: "14px",
                            md: "16px",
                        }}
                        overflow="hidden"
                        bg="white"
                    >
                        {/* HEADER */}

                        <Dialog.Header
                            px={6}
                            py={5}
                            borderBottom="1px solid"
                            borderColor="gray.200"
                        >
                            <Flex
                                justify="space-between"
                                align="center"
                                w="100%"
                            >
                                <HStack gap={3}>
                                    <Ticket
                                        size={20}
                                        weight="fill"
                                        color="#003DC8"
                                    />

                                    <Text
                                        fontWeight="700"
                                        fontSize="lg"
                                    >
                                        Support Ticket
                                    </Text>
                                </HStack>

                                <Box
                                    cursor="pointer"
                                    onClick={onClose}
                                >
                                    <X
                                        size={20}
                                    />
                                </Box>
                            </Flex>
                        </Dialog.Header>

                        {/* BODY */}

                      <Dialog.Body
                        px={{
                            base: 4,
                            md: 6,
                        }}
                        py={{
                            base: 4,
                            md: 6,
                        }}
                    >
                            <VStack
                                align="stretch"
                                gap={6}
                            >
                                {/* CATEGORY */}

                                <Box>
                                    <Text
                                        fontSize="xs"
                                        fontWeight="700"
                                        color="gray.500"
                                        letterSpacing="1px"
                                        mb={3}
                                    >
                                        SELECT
                                        CATEGORY
                                    </Text>

                                   <Grid
                                    templateColumns={{
                                        base: "1fr",
                                        sm: "1fr 1fr",
                                    }}
                                    gap={3}
                                >
                                        {categories.map(
                                            (
                                                item
                                            ) => (
                                                <Button
                                                    key={item.value}
                                                    variant="outline"
                                                    h="48px"
                                                    justifyContent="flex-start"
                                                    borderWidth="1px"
                                                    bg={
                                                        category === item.value
                                                            ? "#E5EEFF"
                                                            : "white"
                                                    }
                                                    borderColor={
                                                        category === item.value
                                                            ? "#003DC8"
                                                            : "gray.200"
                                                    }
                                                    _hover={{
                                                        bg:
                                                            category === item.value
                                                                ? "#E5EEFF"
                                                                : "#F8FAFC",
                                                    }}
                                                    onClick={() =>
                                                        setCategory(item.value)
                                                    }
                                                >
                                                    <HStack>
                                                        <item.icon
                                                            size={16}
                                                            color={
                                                                category === item.value
                                                                    ? "#003DC8"
                                                                    : "#6B7280"
                                                            }
                                                        />

                                                        <Text
                                                            color={
                                                                category === item.value
                                                                    ? "#003DC8"
                                                                    : "#071D54"
                                                            }
                                                            fontWeight={
                                                                category === item.value
                                                                    ? "600"
                                                                    : "500"
                                                            }
                                                        >
                                                            {item.label}
                                                        </Text>
                                                    </HStack>
                                                </Button>
                                            )
                                        )}
                                    </Grid>
                                </Box>

                                {/* SUBJECT */}

                                <Box>
                                    <Text
                                        fontSize="xs"
                                        fontWeight="700"
                                        color="gray.500"
                                        letterSpacing="1px"
                                        mb={2}
                                    >
                                        SUBJECT
                                    </Text>

                                  <Input
    value={subject}
    onChange={(e) =>
        setSubject(
            e.target.value
        )
    }
    placeholder="Summarize the issue"
    h="48px"
/>
                                </Box>

                                {/* MESSAGE */}

                                <Box>
                                    <Text
                                        fontSize="xs"
                                        fontWeight="700"
                                        color="gray.500"
                                        letterSpacing="1px"
                                        mb={2}
                                    >
                                        MESSAGE
                                    </Text>

                                 <Textarea
    value={message}
    onChange={(e) =>
        setMessage(
            e.target.value
        )
    }
    placeholder="Please describe the issue in detail..."
    minH="140px"
/>
                                </Box>

                                {/* ATTACHMENTS */}
                                <Box>
                                    <Text
                                        fontSize="xs"
                                        fontWeight="700"
                                        color="gray.500"
                                        letterSpacing="1px"
                                        mb={2}
                                    >
                                        ATTACHMENTS
                                    </Text>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        hidden
                                        accept=".pdf,.png,.jpg,.jpeg,.log"
                                        onChange={handleFileChange}
                                    />

                                    <Box
                                        border="2px dashed"
                                        borderColor="gray.300"
                                        borderRadius="12px"
                                        bg="#F8FAFC"
                                        py={14}
                                        textAlign="center"
                                        cursor="pointer"
                                        transition="all .2s"
                                        onClick={() => {
                                            if (files.length < 6) {
                                                fileInputRef.current?.click();
                                            }
                                        }}
                                        _hover={{
                                            borderColor: "#2453FF",
                                            bg: "#F5F8FF",
                                        }}
                                    >
                                        <Flex
                                            justify="center"
                                            mb={4}
                                        >
                                            <Upload
                                                size={28}
                                                color="#6B7280"
                                            />
                                        </Flex>

                                        <Text fontWeight="600">
                                            Upload screenshots, logs or PDFs
                                            (6 files maximum)
                                        </Text>

                                        <Text
                                            mt={1}
                                            fontSize="sm"
                                            color="gray.500"
                                        >
                                            PDF, PNG, JPG or LOG
                                            (max 10MB)
                                        </Text>
                                    </Box>

                                    {files.length > 0 && (
                                        <Grid
                                            templateColumns="repeat(3, 1fr)"
                                            gap={3}
                                            mt={4}
                                        >
                                            {files.map(
                                                (
                                                    item,
                                                    index
                                                ) => (
                                                    <Box
                                                        key={index}
                                                        border="1px solid"
                                                        borderColor="gray.200"
                                                        borderRadius="12px"
                                                        p={3}
                                                    >
                                                        {item.preview ? (
                                                            <Flex
                                                                gap={4}
                                                                align="center"
                                                            >
                                                                <Box
                                                                    w="80px"
                                                                    h="80px"
                                                                    borderRadius="10px"
                                                                    overflow="hidden"
                                                                >
                                                                    <Box
                                                                        position="relative"
                                                                    >
                                                                        <Box
                                                                            position="absolute"
                                                                            top="8px"
                                                                            right="8px"
                                                                            bg="white"
                                                                            borderRadius="full"
                                                                            p="4px"
                                                                            cursor="pointer"
                                                                            boxShadow="sm"
                                                                            zIndex={1}
                                                                            onClick={() =>
                                                                                removeFile(index)
                                                                            }
                                                                        >
                                                                            <X
                                                                                size={14}
                                                                                color="#EF4444"
                                                                            />
                                                                        </Box>

                                                                        <img
                                                                            src={item.preview}
                                                                            alt={item.file.name}
                                                                            style={{
                                                                                width: "100%",
                                                                                height: "100%",
                                                                                objectFit: "cover",
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    <img
                                                                        src={
                                                                            item.preview
                                                                        }
                                                                        alt={
                                                                            item
                                                                                .file
                                                                                .name
                                                                        }
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            objectFit:
                                                                                "cover",
                                                                        }}
                                                                    />
                                                                </Box>

                                                                <Box flex="1">
                                                                    <Text
                                                                        fontWeight="600"
                                                                        fontSize="sm"
                                                                    >
                                                                        {
                                                                            item
                                                                                .file
                                                                                .name
                                                                        }
                                                                    </Text>

                                                                    <Text
                                                                        fontSize="xs"
                                                                        color="gray.500"
                                                                    >
                                                                        {(
                                                                            item
                                                                                .file
                                                                                .size /
                                                                            1024
                                                                        ).toFixed(
                                                                            1
                                                                        )}
                                                                        kb
                                                                    </Text>
                                                                </Box>
                                                            </Flex>
                                                        ) : (
                                                            <Flex
                                                                justify="space-between"
                                                                align="center"
                                                            >
                                                                <Text
                                                                    fontWeight="600"
                                                                    fontSize="sm"
                                                                >
                                                                    {
                                                                        item
                                                                            .file
                                                                            .name
                                                                    }
                                                                </Text>

                                                                <Text
                                                                    fontSize="xs"
                                                                    color="gray.500"
                                                                >
                                                                    {(
                                                                        item
                                                                            .file
                                                                            .size /
                                                                        1024
                                                                    ).toFixed(
                                                                        1
                                                                    )}
                                                                    kb
                                                                </Text>
                                                            </Flex>
                                                        )}
                                                    </Box>
                                                )
                                            )}
                                        </Grid>
                                    )}
                                </Box>
                            </VStack>
                        </Dialog.Body>

                        {/* FOOTER */}

                        <Dialog.Footer
                            px={6}
                            py={4}
                            borderTop="1px solid"
                            borderColor="gray.200"
                        >
                            <HStack
                                ml="auto"
                            >
                                <Button
                                    variant="outline"
                                    onClick={
                                        onClose
                                    }
                                >
                                    Cancel
                                </Button>

                               <Button
    bg="#2453FF"
    color="white"
    loading={creating}
    onClick={handleSubmit}
    _hover={{
        bg: "#1D4ED8",
    }}
>
    Submit Ticket
</Button>
                            </HStack>
                        </Dialog.Footer>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default SupportTicketModal;